import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import { makeStyles, alpha } from '@material-ui/core/styles';
import ActionCheck from '@material-ui/icons/CheckCircle';
import AlertError from '@material-ui/icons/ErrorOutline';
import classnames from 'classnames';
import { useTranslate } from 'ra-core';

const useStyles = makeStyles(
  theme => ({
    contentText: {
      minWidth: 400
    },
    confirmPrimary: {
      color: theme.palette.primary.main
    },
    confirmWarning: {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },
    iconPaddingStyle: {
      paddingRight: '0.5em'
    }
  }),
  { name: 'RaConfirm' }
);

/**
 * Confirmation dialog
 *
 * @example
 * <Confirm
 *     isOpen={true}
 *     title="Delete Item"
 *     content="Are you sure you want to delete this item?"
 *     confirm="Yes"
 *     confirmColor="primary"
 *     ConfirmIcon=ActionCheck
 *     CancelIcon=AlertError
 *     cancel="Cancel"
 *     onConfirm={() => { // do something }}
 *     onClose={() => { // do something }}
 * />
 */
const Confirm = ({
  isOpen,
  loading,
  title,
  content,
  confirm,
  cancel,
  confirmColor,
  ConfirmIcon,
  CancelIcon,
  onClose,
  onConfirm,
  classes: classesOverride,
  translateOptions = {},
  showCancel = true
}) => {
  const classes = useStyles({ classes: classesOverride });
  const translate = useTranslate();

  const handleConfirm = useCallback(
    e => {
      e.stopPropagation();
      onConfirm();
    },
    [onConfirm]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">
        {translate(title, { _: title, ...translateOptions })}
      </DialogTitle>
      <DialogContent>
        <Typography
          component="div"
          variant="body1"
          color="textSecondary"
          className={classes.contentText}
        >
          {!React.isValidElement(content)
            ? translate(content, {
                _: content,
                ...translateOptions
              })
            : content}
        </Typography>
      </DialogContent>
      <DialogActions>
        {showCancel && (
          <Button
            disabled={loading}
            onClick={onClose}
            aria-label="cancel-button"
          >
            <CancelIcon className={classes.iconPaddingStyle} />
            {translate(cancel, { _: cancel })}
          </Button>
        )}
        <Button
          disabled={loading}
          onClick={handleConfirm}
          disableFocusRipple
          className={classnames('ra-confirm', {
            [classes.confirmWarning]: confirmColor === 'warning',
            [classes.confirmPrimary]: confirmColor === 'primary'
          })}
          autoFocus
        >
          <ConfirmIcon className={classes.iconPaddingStyle} />
          {translate(confirm, { _: confirm })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

Confirm.propTypes = {
  cancel: PropTypes.string.isRequired,
  classes: PropTypes.object,
  confirm: PropTypes.string,
  confirmColor: PropTypes.string.isRequired,
  ConfirmIcon: PropTypes.elementType.isRequired,
  CancelIcon: PropTypes.elementType.isRequired,
  content: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  isOpen: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired
};

Confirm.defaultProps = {
  cancel: 'ra.action.cancel',
  classes: {},
  confirm: 'ra.action.confirm',
  confirmColor: 'primary',
  ConfirmIcon: ActionCheck,
  CancelIcon: AlertError,
  isOpen: false
};

export default Confirm;
